import Promise from 'bluebird';
import _ from 'lodash';
import numeral from 'numeral';
import tinycolor from 'tinycolor2';
import { CanvasCircle, CanvasDonut, CanvasRectangle, CanvasTriangle, ImageHelper } from '../../../helpers';
import environment from '../../environment';
import { VehicleTypeEnum } from '../vehicleType/constants';
// import 'mapbox.js';

const imgSize = {
    sm: '_sm',
    xs: '_xs'
};

export const pinSizes = [
    { size: 'large', imgSuffix: '', iconSize: [50, 64], iconAnchor: [25, 60], labelAnchor: [25, -32] },
    { size: 'medium', imgSuffix: imgSize.sm, iconSize: [38, 48], iconAnchor: [19, 48], labelAnchor: [19, -24] },
    { size: 'small', imgSuffix: imgSize.xs, iconSize: [28, 28], iconAnchor: [14, 14], labelAnchor: [14, 0] }
];


export const pinBuilder = (() => {
    const _addDonut = (canvas, values, colors, normalize) => {
        if (!_.isArray(values)) {
            values = [values];
        }

        const rgba = ['rgba(30, 136, 229, 0.8)', 'rgba(229, 57, 53, 0.8)', 'rgba(67, 160, 71, 0.8)', 'rgba(0, 172, 193, 0.8)', 'rgba(216, 27, 96, 0.8)', 'rgba(255, 102, 0, 0.8)', 'rgba(192, 202, 51, 0.8)', 'rgba(255, 193, 7, 0.8)', 'rgba(103, 58, 183, 0.8)', 'rgba(0, 137, 123, 0.8)'];

        if (!colors) {
            colors = _.size(values) < 2
                ? ['rgba(0,0,0,.25)']
                : _.map(_.range(0, _.size(values)), i => rgba[i % 10]);
        }

        const data = {
            values,
            colors
        };

        const lineWidth = 6;

        const canvasDonut = new CanvasDonut(canvas, normalize);
        canvasDonut.set((canvas.width / 2), (canvas.height / 2) - 6, (canvas.width / 2) - (lineWidth * 0.48), 0.7 * Math.PI, 2.3 * Math.PI, lineWidth, 'transparent');
        canvasDonut.draw(data);
    };

    const _addRectangle = (canvas, size, radius, fillStyle) => {
        const canvasRectangle = new CanvasRectangle(canvas);
        canvasRectangle.set(0, 0, size.width, size.height, radius, fillStyle);
        canvasRectangle.draw();
    };

    const _addCircle = (text, canvas, x, y, radius, fillStyle, lineWidth, strokeStyle, withShadow, fontSize) => {
        const canvasCircle = new CanvasCircle(canvas);
        canvasCircle.set(x, y, radius, fillStyle, lineWidth, strokeStyle, withShadow, fontSize);
        canvasCircle.draw();
        canvasCircle.text(text);
    };

    const _addTriangle = (text, canvas, x, y, width, height, rotate, fillStyle) => {
        const canvasCircle = new CanvasTriangle(canvas);
        canvasCircle.set(x, y, width, height, rotate, fillStyle);
        canvasCircle.draw();
        if (text) { canvasCircle.text(`+${text}`); }
    };

    const createPinFromImage = (image, text, colors, width, height) => {
        return new Promise((resolve, reject) => {
            width = Math.max(1, Math.floor(width));
            height = Math.max(1, Math.floor(height));

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0, width, height);

            const color = ImageHelper.getAverageRGB(image);
            if ((text || {}).value) {
                ctx.textAlign = 'center';
                ctx.fillStyle = tinycolor(colors.body.color || `rgb(${color.r}, ${color.g}, ${color.b})`).getBrightness() < 160 ? '#FFF' : '#303030';
                ctx.font = `bold 12px 'Roboto Condensed', Arial, Sans-serif`;
                ctx.fillText(text.value, width / 2, (height / 2) + 4);
            }

            resolve(canvas.toDataURL('image/png'));
        });
    };

    const create = async ({ url, text, colors, height, width }) => {
        return new Promise((resolve, reject) => {
            ImageHelper.base64ToSvgAsync(url).then(svg => {
                _.forEach(colors, (val, key) => {
                    svg.select(`#${key}`).style('fill', val);
                });

                _.forEach(text, (val, key) => {
                    svg.select(`#${key}`).text(val);
                });

                ImageHelper.svgToImageAsync(svg.node()).then(i => {
                    createPinFromImage(i, text, colors, width, height).then(image => {
                        resolve(image);
                    });
                });
            });
        });
    };

    create.client = async (color, width = 14, height = 18, url = require('../../../assets/images/pinbuilder/pin_client.svg')) => {
        return L.icon({
            iconUrl: await create({
                url,
                colors: { header: color, body: color },
                height,
                width
            }),
            iconSize: L.point(width, height),
            iconAnchor: L.point(width / 2, height / 2)
        });
    };

    create.circle = async ({ value, color, width = 29, height = 29, url = require('../../../assets/images/pinbuilder/pin_circle.svg') }) => {
        return L.icon({
            iconUrl: await create({
                url,
                text: { value },
                colors: { body: color },
                height,
                width
            }),
            iconSize: L.point(width, height),
            iconAnchor: L.point(width / 2, height / 2)
        });
    };

    create.circle.small = async (options) => {
        return create.circle(_.assign({}, options, { url: require('../../../assets/images/pinbuilder/pin_circle_small.svg') }));
    };

    create.pin = {};

    create.pin.imageWithValue = (data, image) => {
        const width = Math.max(1, Math.floor(image.width));
        const height = Math.max(1, Math.floor(image.height));

        const canvas = L.DomUtil.create('canvas', '');
        canvas.width = Math.round(width);
        canvas.height = Math.round(height);

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        let value = data.value;
        if (Number(value) > 9999) {
            const lang = numeral.locale();
            numeral.locale('en');
            value = numeral(value).format('0a');
            numeral.locale(lang);
        }

        const color = ImageHelper.getAverageRGB(image).toRgb();
        const ctx = canvas.getContext('2d');
        ctx.textAlign = 'center';
        ctx.fillStyle = tinycolor(data.color || `rgb(${color.r}, ${color.g}, ${color.b})`).getBrightness() < 160 ? '#FFF' : '#303030';

        ctx.drawImage(image, 0, 0, width, height);
        if (data.total > 0) {
            ctx.font = 'bold 13px Arial, Sans-serif';
            ctx.fillText(value, centerX, centerY - 10);
            ctx.font = '10px Arial, Sans-serif';
            value = numeral(data.value / data.total).format('0,0.0%');
            ctx.fillText(value, centerX, centerY + 2);
            _addDonut(canvas, (data.value / data.total), data.colors, false);
        } else if (data.isCurrency) {
            ctx.font = '10px Arial, Sans-serif';
            value = numeral(data.value)
                    .format('(0.00 a)')
                    .replace(/milhões/, 'mi');
            ctx.fillText(value, centerX, centerY - 2);
        } else {
            ctx.font = `bold 15px Arial, Sans-serif`;
            if (centerX === centerY) {
                ctx.fillText(value, centerX, centerY + 5);
            } else {
                ctx.fillText(value, centerX, centerY - 2);
            }
            if (_.size(data.percent)) {
                _addDonut(canvas, data.percent, data.colors);
            }
        }

        return L.icon({
            iconUrl: canvas.toDataURL('image/png'),
            iconSize: L.point(canvas.width, canvas.height),
            iconAnchor: L.point(centerX, canvas.height - 4)
        });
    };

    create.pin.pure = (data) => {
        const { width, height, value, color, imgSuffix } = data;
        const isIconXs = imgSuffix === 'xs';
        const fillStyle = color;

        const canvas = L.DomUtil.create('canvas', '');
        canvas.width = width;
        canvas.height = height;

        const iconOptions = {
            lg: {
                radius: 22,
                triangleCenterY: 56,
                fontSize: 16,
            },
            sm: {
                radius: 18,
                triangleCenterY: 42,
                fontSize: 16,
            },
            xs: {
                radius: 12,
                triangleCenterY: 36,
                fontSize: 12,
            }
        };

        const { radius, triangleCenterY, fontSize } = iconOptions[!imgSuffix ? 'lg' : imgSuffix];
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        if(!isIconXs) {
            _addTriangle(null, canvas, centerX, triangleCenterY, 20, 10, 0, '#FFFFFF');
        }

        _addCircle(value, canvas, centerX , centerY, radius, fillStyle, 2, '#FFFFFF', true, fontSize);

        return L.icon({
            iconUrl: canvas.toDataURL('image/png'),
            iconSize: L.point(canvas.width, canvas.height),
            iconAnchor: L.point(30, centerY)
        });
    };

    create.pin.cluster = (data) => {
        const sizeBrands = _.size(data.images);

        const height = 38;
        const fillStyle = '#34495f';
        const imageSize = 28;
        const overlap = 10;

        const canvas = L.DomUtil.create('canvas', '');
        const ctx = canvas.getContext('2d');

        let requiredWidth = 0;

        let xOffset = 5;

        _.forEach(_.take(data.images, 3), (image, i) => {
            ctx.drawImage(ImageHelper.get(image), xOffset, 5, imageSize, imageSize);

            requiredWidth = xOffset + imageSize;

            xOffset += imageSize - overlap;
        });

        const hasCircle = sizeBrands > 3 || data.total;
        if (hasCircle) {
            const radius = imageSize / 2;
            const circleX = xOffset + radius - 5;
            const circleY = radius + 5;
            _addCircle(data.total ? data.value : `+${data.value - 3}`, canvas, circleX, circleY, radius, fillStyle);
            requiredWidth = circleX + radius + 5;
        }

        const extraPadding = 5;
        const finalCanvasWidth = Math.max(requiredWidth + extraPadding, 50);
        canvas.width = finalCanvasWidth;
        canvas.height = 44;

        const centerY = canvas.height / 2;
        _addTriangle(null, canvas, 20, 38, 20, 10, 0, '#293a4f');
        _addRectangle(canvas, { width: finalCanvasWidth, height: height }, 20, fillStyle);

        xOffset = 5;
        _.forEach(_.take(data.images, 3), (image, i) => {
            ctx.drawImage(ImageHelper.get(image), xOffset, 5, imageSize, imageSize);
            xOffset += imageSize - overlap;
        });

        if (hasCircle) {
            const radius = imageSize / 2;
            const circleX = xOffset + radius - 5;
            const circleY = radius + 5;
            _addCircle(data.total ? data.value : `+${data.value - 3}`, canvas, circleX, circleY, radius, fillStyle);
        }

        return L.icon({
            iconUrl: canvas.toDataURL('image/png'),
            iconSize: L.point(canvas.width, canvas.height),
            iconAnchor: L.point(20, centerY)
        });
    };

    create.buffer = {};

    create.buffer.withValue = data => {
        const canvas = L.DomUtil.create('canvas', '');
        canvas.width = 52;
        canvas.height = 52;

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.arc(centerX, centerY, centerX - 2, 0, 2 * Math.PI);
        ctx.fillStyle = data.color;
        ctx.fill();
        ctx.strokeStyle = tinycolor(data.color).darken(10).toString();
        ctx.stroke();

        const value = Number(data.value);

        ctx.textAlign = 'center';
        ctx.fillStyle = tinycolor(data.color).isDark() ? '#FFF' : tinycolor('#000').setAlpha(0.87).toString();
        ctx.font = 'bold 15px Arial, Sans-serif';
        ctx.fillText(value, centerX, 32);

        return L.icon({
            iconUrl: canvas.toDataURL('image/png'),
            iconSize: L.point(canvas.width, canvas.height),
            iconAnchor: L.point(centerX, centerY)
        });
    };

    const preload = () => {
        _.forEach([
            require('../../../assets/images/pinbuilder/pin_client.svg'),
            require('../../../assets/images/pinbuilder/pin_circle.svg'),
            require('../../../assets/images/pinbuilder/pin_circle_small.svg')
        ], (url) => {
            ImageHelper.getAsync(url);
        });

        _.forEach(environment.get('CONFIG.STORES_VEHICLE_TYPE'), m => {
            const folderVehicleType = m.vehicleType === VehicleTypeEnum.Moto
                ? "/motos/"
                : "/";

            const url = `${environment.get('IMAGES_HOST')}${folderVehicleType}pin_${String(m.name).toUnderscoreCase()}_xs.png`;
            ImageHelper.getAsync(url);
        });

        if (environment.get("CONFIG.IS_AFTER_SALES")) {
            const urlBase = `${environment.get('IMAGES_HOST')}/aftersales`;

            _.forEach(environment.get('CONFIG.FILTERS.NETWORKS').filter(x => x.isMain), x => {
                const url = `${urlBase}/pin_${String(x.networkName).toUnderscoreCase()}_xs.png`;
                ImageHelper.getAsync(url);
            });
        }
    };

    // API
    return {
        create,
        preload
    };
})();



// WEBPACK FOOTER //
// ./src/scripts/core/map/pinBuilder.js